import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { faUser, faUsers, faArchive } from '@fortawesome/free-solid-svg-icons';
import { ConsultationService, sharedApiClient } from '../../fetcher';
import {
  useNotifications,
  useAuth,
  UserAbility,
  IdentityService,
  createCoalescer,
  isAbortError,
} from '@medprompt/shared';
import {
  ScribeViewMode,
  consultationBelongsInView,
  mergeFetchedConsultation,
  shouldFetchConsultationUpdate,
  shouldRefreshConsultationSearch,
} from './scribeListMembership';

export { ScribeViewMode };

const SCRIBE_VIEW_MODE_KEY = 'scribeViewMode';

const sortByNewest = (items) =>
  [...(items || [])].sort((a, b) => {
    const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
    const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
    return bTime - aTime;
  });

const useDropdown = (initialValue) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(initialValue);
  const dropdownRef = useRef(null);

  const toggle = (otherDropdowns = []) => {
    setIsOpen(!isOpen);
    otherDropdowns.forEach(dropdown => dropdown.setIsOpen(false));
  };

  const handleSelect = (option) => {
    setSelected(option);
    setIsOpen(false);
  };

  return {
    isOpen,
    setIsOpen,
    selected,
    setSelected,
    toggle,
    handleSelect,
    dropdownRef
  };
};

/** The consultations for the current view mode or search, kept current from the update stream. */
const useScribeList = () => {
  const { user } = useAuth();
  const { addNotification, addConsultationUpdateCallback, addReconnectCallback } = useNotifications();
  const consultationService = useMemo(() => new ConsultationService(sharedApiClient), []);
  const identityService = useMemo(() => new IdentityService(), []);

  const hasConsultReadAll = Boolean(user?.authorities?.includes(UserAbility.CONSULT_READ_ALL.key));
  const hasConsultWrite = Boolean(user?.authorities?.includes(UserAbility.CONSULT_WRITE.key));

  const [consultations, setConsultations] = useState([]);
  const [userIdToClinicianData, setUserIdToClinicianData] = useState(new Map());
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [clearSearchBarFunction, setClearSearchBarFunction] = useState(null);
  const listAbortRef = useRef(null);
  const patchAbortRef = useRef(null);
  const searchAbortRef = useRef(null);
  const searchRefreshAbortRef = useRef(null);
  const rerunSearchRef = useRef(() => {});
  // Ids with a list fetch in flight or not yet committed, so a follow-up event is not dropped.
  const patchIdsRef = useRef(new Set());

  const [viewMode, setViewMode] = useState(() => {
    const stored = sessionStorage.getItem(SCRIBE_VIEW_MODE_KEY);
    if (stored && Object.values(ScribeViewMode).includes(stored)) {
      return stored;
    }
    return hasConsultReadAll ? ScribeViewMode.ALL_CONSULTATIONS : ScribeViewMode.MY_CONSULTATIONS;
  });
  const viewModeDropdown = useDropdown(viewMode);

  useEffect(() => {
    sessionStorage.setItem(SCRIBE_VIEW_MODE_KEY, viewMode);
  }, [viewMode]);

  // Full list fetch. Reserved for mount, view-mode change, and reconnect.
  // A live update patches one row instead.
  // No "already fetching" guard: it would drop the newest request while keeping the one the abort
  // below has just cancelled, leaving the list permanently empty. Newest request wins.
  const refreshConsultations = useCallback(async () => {
    patchIdsRef.current.clear();
    patchAbortRef.current?.abort();
    listAbortRef.current?.abort();
    const controller = new AbortController();
    listAbortRef.current = controller;
    const options = { signal: controller.signal };

    try {
      let data;

      switch (viewMode) {
        case ScribeViewMode.MY_CONSULTATIONS:
          data = await consultationService.fetchAssignedConsultations(options);
          break;
        case ScribeViewMode.ALL_CONSULTATIONS:
          if (hasConsultReadAll) {
            data = await consultationService.fetchActiveConsultations(options);
          } else {
            data = await consultationService.fetchAssignedConsultations(options);
          }
          break;
        case ScribeViewMode.ARCHIVE:
          data = await consultationService.fetchArchiveConsultations(options);
          break;
        default:
          data = [];
      }

      if (listAbortRef.current !== controller) {
        return; // a newer fetch has taken over
      }
      setConsultations(sortByNewest(data));
      rerunSearchRef.current();
    } catch (error) {
      if (isAbortError(error)) return;
      console.error('Error fetching consultations:', error);
      addNotification('scribe-fetch-error', 'Failed to load consultations', { isError: true });
    } finally {
      if (listAbortRef.current === controller) {
        listAbortRef.current = null;
      }
    }
  }, [viewMode, hasConsultReadAll, consultationService, addNotification]);

  useEffect(() => {
    refreshConsultations();
  }, [refreshConsultations]);

  useEffect(() => () => {
    listAbortRef.current?.abort();
    patchAbortRef.current?.abort();
    searchAbortRef.current?.abort();
    searchRefreshAbortRef.current?.abort();
  }, []);

  const refreshConsultationsRef = useRef(refreshConsultations);
  useEffect(() => {
    refreshConsultationsRef.current = refreshConsultations;
  }, [refreshConsultations]);

  const listSyncRef = useRef({
    consultations,
    viewMode,
    userId: null,
    searchResults,
    isSearchActive,
    searchQuery,
  });
  listSyncRef.current = {
    consultations,
    viewMode,
    userId: user?.userId ?? null,
    searchResults,
    isSearchActive,
    searchQuery,
  };

  // One list fetch per id. The view predicate then keeps, inserts, or removes the row.
  const patchConsultations = useCallback(async (consultationIds) => {
    patchAbortRef.current?.abort();
    const controller = new AbortController();
    patchAbortRef.current = controller;

    const results = await Promise.all(consultationIds.map(async (consultationId) => {
      try {
        return await consultationService.fetchConsultationListData(
          consultationId, { signal: controller.signal });
      } catch (error) {
        return isAbortError(error) ? undefined : null;
      }
    }));

    if (controller.signal.aborted) return;
    if (patchAbortRef.current === controller) {
      patchAbortRef.current = null;
    }

    const { viewMode: mode, userId, consultations: shown } = listSyncRef.current;
    const decisions = consultationIds.map((consultationId, index) => {
      const fetched = results[index];
      if (fetched === undefined) {
        return { consultationId, skip: true };
      }
      const belongs = fetched == null ? false : consultationBelongsInView(fetched, mode, userId);
      return { consultationId, fetched, belongs, skip: false };
    });

    setConsultations(previous => {
      let next = previous;
      decisions.forEach((decision) => {
        if (decision.skip) return;
        next = mergeFetchedConsultation(next, decision.consultationId, decision.fetched, decision.belongs);
      });
      return next === previous ? previous : sortByNewest(next);
    });

    decisions.forEach((decision) => {
      if (decision.skip) return;
      const stays = decision.belongs === true
        || (decision.belongs == null && shown.some(item => item.consultationId === decision.consultationId));
      if (!stays) {
        patchIdsRef.current.delete(decision.consultationId);
      }
    });
  }, [consultationService]);

  const patchHandlerRef = useRef(patchConsultations);
  useEffect(() => {
    patchHandlerRef.current = patchConsultations;
  }, [patchConsultations]);

  useEffect(() => {
    const ids = patchIdsRef.current;
    if (ids.size === 0) return;
    for (const id of ids) {
      if (consultations.some(item => item.consultationId === id)) {
        ids.delete(id);
      }
    }
  }, [consultations]);

  const patchCoalescerRef = useRef(null);
  const searchCoalescerRef = useRef(null);
  useEffect(() => {
    patchCoalescerRef.current = createCoalescer((ids) => patchHandlerRef.current(ids));
    searchCoalescerRef.current = createCoalescer(() => rerunSearchRef.current());
    return () => {
      patchCoalescerRef.current?.cancel();
      searchCoalescerRef.current?.cancel();
    };
  }, []);

  useEffect(() => {
    if (!addConsultationUpdateCallback) return undefined;
    return addConsultationUpdateCallback((consultationId, statusTransition) => {
      if (!consultationId) return;
      if (statusTransition?.to === 'NON_EXISTENT') {
        patchIdsRef.current.delete(consultationId);
        setConsultations(previous => previous.filter(item => item.consultationId !== consultationId));
        setSearchResults(previous => previous.filter(item => item.consultationId !== consultationId));
        return;
      }
      const {
        consultations: shown,
        viewMode: mode,
        userId,
        searchResults: results,
        isSearchActive: searching,
      } = listSyncRef.current;
      const isTracked = shown.some(item => item.consultationId === consultationId)
        || patchIdsRef.current.has(consultationId);
      if (shouldFetchConsultationUpdate(isTracked, statusTransition, mode, userId)) {
        patchIdsRef.current.add(consultationId);
        patchCoalescerRef.current?.push(consultationId);
      }
      const inSearch = results.some(item => item.consultationId === consultationId);
      if (searching && shouldRefreshConsultationSearch(inSearch, statusTransition)) {
        searchCoalescerRef.current?.push(consultationId);
      }
    });
  }, [addConsultationUpdateCallback]);

  useEffect(() => {
    if (!addReconnectCallback) return undefined;
    return addReconnectCallback(() => refreshConsultationsRef.current());
  }, [addReconnectCallback]);

  // Fetch active clinician users and cache them
  useEffect(() => {
    const fetchClinicians = async () => {
      try {
        const clinicians = await identityService.fetchActiveClinicUsers();
        if (Array.isArray(clinicians)) {
          const clinicianMap = new Map();
          clinicians.forEach(clinician => {
            if (clinician.userId) {
              clinicianMap.set(clinician.userId, clinician);
            }
          });
          setUserIdToClinicianData(clinicianMap);
        }
      } catch (error) {
        console.error('Error fetching clinic users:', error);
      }
    };

    fetchClinicians();
  }, [identityService]);

  const rerunSearch = useCallback(async () => {
    const { isSearchActive: searching, searchQuery: query } = listSyncRef.current;
    if (!searching || !query?.trim()) return;

    searchRefreshAbortRef.current?.abort();
    const controller = new AbortController();
    searchRefreshAbortRef.current = controller;

    try {
      const results = await consultationService.searchConsultations(query, { signal: controller.signal });
      if (searchRefreshAbortRef.current !== controller) return;
      const current = listSyncRef.current;
      if (!current.isSearchActive || current.searchQuery !== query) return;
      setSearchResults(Array.isArray(results) ? results : []);
    } catch (error) {
      if (!isAbortError(error)) {
        console.error('Error refreshing consultation search:', error);
      }
    } finally {
      if (searchRefreshAbortRef.current === controller) {
        searchRefreshAbortRef.current = null;
      }
    }
  }, [consultationService]);
  rerunSearchRef.current = rerunSearch;

  // Cancels the previous query so results always match what is on screen, rather than whichever
  // response happens to arrive last.
  const handleConsultationSearch = useCallback(async (query, callback) => {
    searchAbortRef.current?.abort();
    searchRefreshAbortRef.current?.abort();

    if (!query || !query.trim()) {
      searchAbortRef.current = null;
      setSearchResults([]);
      setIsSearchActive(false);
      setSearchQuery('');
      if (callback) {
        callback([]);
      }
      return;
    }

    const controller = new AbortController();
    searchAbortRef.current = controller;

    try {
      const results = await consultationService.searchConsultations(
        query, { signal: controller.signal });
      if (searchAbortRef.current !== controller) {
        return; // a newer query has taken over
      }
      setSearchResults(Array.isArray(results) ? results : []);
      setIsSearchActive(true);
      setSearchQuery(query);
      if (callback) {
        callback(results);
      }
    } catch (error) {
      if (isAbortError(error)) {
        return; // superseded; the newer query owns the results
      }
      console.error('Error searching consultations:', error);
      addNotification('scribe-search-error', 'Consultation search failed', { isError: true });
      setSearchResults([]);
      setIsSearchActive(false);
      setSearchQuery('');
      if (callback) {
        callback([]);
      }
    } finally {
      if (searchAbortRef.current === controller) {
        searchAbortRef.current = null;
      }
    }
  }, [consultationService, addNotification]);

  const handleClearSearch = useCallback(() => {
    searchRefreshAbortRef.current?.abort();
    setSearchResults([]);
    setIsSearchActive(false);
    setSearchQuery('');
    if (clearSearchBarFunction) {
      clearSearchBarFunction();
    }
  }, [clearSearchBarFunction]);

  const visibleConsultations = isSearchActive
    ? searchResults
    : consultations.filter(item => consultationBelongsInView(item, viewMode, user?.userId) !== false);

  const viewModeOptions = useMemo(() => {
    const options = [
      { id: ScribeViewMode.MY_CONSULTATIONS, name: 'My Consultations', icon: faUser }
    ];

    if (hasConsultReadAll) {
      options.push({ id: ScribeViewMode.ALL_CONSULTATIONS, name: 'All Consultations', icon: faUsers });
    }

    options.push({ id: ScribeViewMode.ARCHIVE, name: 'Archive', icon: faArchive });

    return options;
  }, [hasConsultReadAll]);

  const currentViewModeOption = viewModeOptions.find(opt => opt.id === viewMode) || viewModeOptions[0];

  const selectViewMode = (option) => {
    setViewMode(option.id);
    viewModeDropdown.setIsOpen(false);
  };

  return {
    consultationService,
    user,
    hasConsultReadAll,
    hasConsultWrite,
    visibleConsultations,
    refreshConsultations,
    userIdToClinicianData,
    viewMode,
    viewModeOptions,
    viewModeDropdown,
    currentViewModeName: currentViewModeOption.name,
    currentViewModeIcon: currentViewModeOption.icon,
    selectViewMode,
    isSearchActive,
    searchResults,
    searchQuery,
    setClearSearchBarFunction,
    handleConsultationSearch,
    handleClearSearch,
  };
};

export default useScribeList;
